import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { tokenStorage } from '@/utils/storage';

interface AuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    tokenStorage.getToken()
  );

  useEffect(() => {
    // Sync on storage change (other tabs)
    const handler = () => setToken(tokenStorage.getToken());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const login = (newToken: string) => {
    tokenStorage.setToken(newToken);
    setToken(newToken);
  };

  const logout = () => {
    tokenStorage.removeToken();
    setToken(null);
  };

  return (
    <AuthContext.Provider
      value={{ token, isAuthenticated: !!token, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
